import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Avatar,
    Divider,
    Alert,
    Stack,
    Paper,
    LinearProgress,
} from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { Cancel } from "@mui/icons-material";
import { logout } from "../../../lib/authenticate";

interface PendingApprovalModalProps {
    open: boolean;
    onClose: () => void;
}

export default function PendingApprovalModal({
    open,
    onClose,
}: PendingApprovalModalProps) {
    const [remainingTime, setRemainingTime] = useState(60);

    useEffect(() => {
        if (!open) return;

        // Set timeout to logout after 1 minute (60,000 milliseconds)
        const logoutTimer = setTimeout(() => {
            logout();
        }, 60000);

        // Cleanup timeout if modal closes before timeout completes
        return () => clearTimeout(logoutTimer);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        setRemainingTime(60);

        const interval = setInterval(() => {
            setRemainingTime((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [open]);
    return (
        <Dialog
            open={open}
            onClose={() => { }} // Prevent manual closing
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 },
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                        <HourglassEmptyIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="600">
                        Awaiting Manager Approval
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        Your request to join this schedule is currently pending manager approval.
                    </Typography>

                    <Alert
                        severity="error"
                        icon={<Cancel />}
                        sx={{
                            bgcolor: "error.50",
                            "& .MuiAlert-icon": {
                                color: "error.main",
                            },
                        }}
                    >
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                            What happens next?
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            • The schedule manager will review your request
                        </Typography>
                        <Typography variant="caption" component="div">
                            • You'll be notified once your request is approved or denied
                        </Typography>
                        <Typography variant="caption" component="div">
                            • Once approved, you'll have full access to the schedule
                        </Typography>
                    </Alert>

                    <Divider />

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
                            WHAT YOU CAN DO
                        </Typography>
                        <Typography variant="body2">
                            • You may need to logout and log back in if you are not seeing data after the request was approved
                        </Typography>
                        <Typography variant="body2">
                            • Contact your schedule manager if you have questions
                        </Typography>
                    </Paper>

                    <Divider />

                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Automatic Logout in
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color={remainingTime <= 10 ? 'error.main' : 'text.secondary'}>
                                {remainingTime}s
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={(remainingTime / 60) * 100}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    backgroundColor: remainingTime <= 10 ? 'error.main' : 'primary.main',
                                    transition: 'background-color 0.3s ease',
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: "grey.50" }}>
            </DialogActions>
        </Dialog>
    );
}
